/**
 * EditAdModal Component for Admin
 * Modal wrapper for editing ads using AdForm
 */

import { useState } from 'react';
import Modal from '../../common/Modal';
import AdForm from '../../advertising/AdForm';
import type { AdFormData } from '../../advertising/AdForm';
import './EditModal.css';

const API_BASE = (import.meta.env as { VITE_API_URL?: string }).VITE_API_URL || 'http://localhost:8787';

interface Ad {
  id: string;
  title: string;
  description?: string;
  category: string;
  region: string;
  district?: string;
  storeName?: string;
  phone?: string;
  address?: string;
  priceRange?: string;
  imageUrls?: string[];
  tags?: string[];
  status?: string;
}

interface EditAdModalProps {
  isOpen: boolean;
  onClose: () => void;
  ad: Ad | null;
  onSave: (updatedItem?: Ad) => void;
}

const EditAdModal = ({ isOpen, onClose, ad, onSave }: EditAdModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (data: AdFormData) => {
    if (!ad) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE}/api/admin/content/ads/${ad.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error?.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // Pass updated item data to onSave for local update
        onSave(result.data);
        onClose();
      } else {
        throw new Error(result.error?.message || '更新失敗');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '更新廣告時發生錯誤';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!ad) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="編輯廣告"
      size="large"
    >
      <AdForm
        error={error ?? undefined}
        initialData={{
          title: ad.title,
          description: ad.description ?? undefined,
          category: ad.category,
          region: ad.region,
          district: ad.district ?? undefined,
          storeName: ad.storeName ?? undefined,
          phone: ad.phone ?? undefined,
          address: ad.address ?? undefined,
          priceRange: ad.priceRange ?? undefined,
          imageUrls: ad.imageUrls ?? undefined,
          tags: ad.tags ?? undefined,
        }}
        onSubmit={handleSubmit}
        isEditing={true}
        isLoading={isLoading}
      />
    </Modal>
  );
};

export default EditAdModal;
